/**
 * Hello Mrs cart policy — kit product IDs: only one kit line (either ID), qty 1.
 * All other products add and update normally (gift cards, etc.).
 */
(function () {
  const RESTRICTED_IDS = new Set([10353272553788, 10118155141436]);
  const MSG = {
    kit_exclusive: {
      title: 'One kit per cart',
      text: 'Remove the other kit to add this one.',
    },
    kit_already: {
      title: 'One kit per cart',
      text: 'This kit is already in your cart.',
    },
    kit_qty: {
      title: 'Quantity updated',
      text: 'Kit quantity is limited to 1.',
    },
  };

  let cartItems = [];
  let enforcing = false;
  let toastHideTimer;

  const isRestricted = (productId) => RESTRICTED_IDS.has(Number(productId));
  const restrictedInCart = () => cartItems.filter((item) => isRestricted(item.product_id));

  const fetchCart = () => fetch('/cart.js').then((res) => res.json());
  const changeLineQty = (line, quantity) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });

  function syncCart(cart) {
    cartItems = cart?.items ?? [];
    return cart;
  }

  function getAddFormProductId(form) {
    const productInfo = form.closest('product-info');
    if (productInfo?.dataset?.productId) return Number(productInfo.dataset.productId);
    const withId = form.closest('[data-product-id]');
    if (withId?.dataset?.productId) return Number(withId.dataset.productId);
    return null;
  }

  function showHelloCartWarning(key) {
    const copy = MSG[key] || MSG.kit_exclusive;
    let toast = document.getElementById('hello-cart-policy-toast');

    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'hello-cart-policy-toast';
      toast.className = 'hello-cart-policy-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.innerHTML = `
        <p class="hello-cart-policy-toast__title"></p>
        <p class="hello-cart-policy-toast__text"></p>
        <button type="button" class="hello-cart-policy-toast__close" aria-label="Close">×</button>
      `;
      document.body.appendChild(toast);
      toast.querySelector('.hello-cart-policy-toast__close').addEventListener('click', () => hideToast(toast));
    }

    toast.querySelector('.hello-cart-policy-toast__title').textContent = copy.title;
    toast.querySelector('.hello-cart-policy-toast__text').textContent = copy.text;

    clearTimeout(toastHideTimer);
    toast.classList.remove('hello-cart-policy-toast--visible');
    requestAnimationFrame(() => toast.classList.add('hello-cart-policy-toast--visible'));
    toastHideTimer = setTimeout(() => hideToast(toast), 5000);
  }

  function hideToast(toast) {
    toast.classList.remove('hello-cart-policy-toast--visible');
  }

  function lockProductQuantityInputs() {
    document.querySelectorAll('product-info[data-product-id]').forEach((root) => {
      if (!isRestricted(root.dataset.productId)) return;
      root.querySelectorAll('input[name="quantity"]').forEach((input) => {
        input.value = '1';
        input.readOnly = true;
        input.min = '1';
        input.max = '1';
      });
    });
  }

  function getKitAddBlockReason(productId) {
    if (!isRestricted(productId) || !cartItems.length) return null;

    if (
      cartItems.some(
        (item) => isRestricted(item.product_id) && Number(item.product_id) === Number(productId)
      )
    ) {
      return 'kit_already';
    }

    if (
      cartItems.some(
        (item) => isRestricted(item.product_id) && Number(item.product_id) !== Number(productId)
      )
    ) {
      return 'kit_exclusive';
    }

    return null;
  }

  function blockKitAdd(form, reason) {
    showHelloCartWarning(reason);
  }

  function patchProductForm() {
    const ProductForm = customElements.get('product-form');
    if (!ProductForm?.prototype?.onSubmitHandler) return false;
    if (ProductForm.prototype.onSubmitHandler.__helloPatched) return true;

    const originalSubmit = ProductForm.prototype.onSubmitHandler;
    ProductForm.prototype.onSubmitHandler = function (evt) {
      const reason = getKitAddBlockReason(getAddFormProductId(this.form));
      if (reason) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        blockKitAdd(this.form, reason);
        return;
      }
      return originalSubmit.call(this, evt);
    };
    ProductForm.prototype.onSubmitHandler.__helloPatched = true;
    return true;
  }

  function bindAddToCartForms() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.helloCartBound) return;
      form.dataset.helloCartBound = 'true';
      form.addEventListener('submit', onAddToCartSubmit, true);
    });
  }

  async function enforceCartLimits() {
    if (enforcing) return;
    enforcing = true;

    try {
      const cart = syncCart(await fetchCart());
      const kits = restrictedInCart();
      if (!kits.length) return;

      let needsReload = false;
      const kitProductIds = new Set(kits.map((item) => item.product_id));

      if (kitProductIds.size > 1) {
        showHelloCartWarning('kit_exclusive');
        let keptOneKit = false;
        for (let i = cart.items.length - 1; i >= 0; i--) {
          const item = cart.items[i];
          if (!isRestricted(item.product_id)) continue;
          if (!keptOneKit) {
            keptOneKit = true;
            continue;
          }
          await changeLineQty(i + 1, 0);
          needsReload = true;
        }
      }

      const overQty = cart.items.find((item) => isRestricted(item.product_id) && item.quantity > 1);
      if (overQty) {
        showHelloCartWarning('kit_qty');
        await changeLineQty(cart.items.indexOf(overQty) + 1, 1);
        needsReload = true;
      }

      if (needsReload) window.location.reload();
    } finally {
      enforcing = false;
    }
  }

  function onAddToCartSubmit(event) {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const reason = getKitAddBlockReason(getAddFormProductId(form));
    if (!reason) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    blockKitAdd(form, reason);
  }

  function init() {
    lockProductQuantityInputs();
    bindAddToCartForms();
    if (!patchProductForm()) {
      customElements.whenDefined('product-form').then(patchProductForm);
    }
    fetchCart().then(syncCart).then(() => enforceCartLimits());

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        fetchCart().then(syncCart).then(() => enforceCartLimits());
      });
    }

    document.addEventListener('shopify:section:load', () => {
      lockProductQuantityInputs();
      bindAddToCartForms();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
